import React, { useEffect, useMemo, useState } from "react"
import moment from "moment"
import { useSelector } from "react-redux"
import { useHistory, withRouter } from "react-router-dom"
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap"

import ButtonMain from "components/Common/Button"
import { showMessage } from "components/Notification"
import {
  addProviderHoliday,
  deleteProviderHoliday,
  deleteProviderParticularDay,
  fetchProviderAvailability,
  saveProviderParticularDay,
  updateProviderDefaultTiming,
  updateProviderSlotDuration,
} from "helpers/api_helper"
import { getUserDetails } from "store/slices"
import LeftArrow from "../../assets/icons/ArrowLeft.png"
import AvailabilityTimingModal, {
  validateTiming,
} from "./components/AvailabilityTimingModal"
import AvailabilityPicker from "./components/AvailabilityPicker"
import AvailabilityDurationInput from "./components/AvailabilityDurationInput"
import "./components/Availability.scss"

const PROVIDER_TYPE = "vet"
const DEFAULT_TIMING = {
  startTime: "09:00",
  endTime: "18:00",
  lunchStart: "13:00",
  lunchEnd: "14:00",
}

const getErrorMessage = error =>
  error?.response?.data?.message ||
  error?.message ||
  "Something went wrong. Please try again."

const requireSuccessfulResponse = response => {
  if (!response || response.status !== 1 || !response.data) {
    throw new Error(
      response?.message || "The server could not save this change."
    )
  }
  return response
}

const displayDate = date =>
  moment(date, "YYYY-MM-DD", true).format("DD MMM YYYY")
const displayTime = time => moment(time, "HH:mm", true).format("hh:mm A")

const Timings = () => {
  document.title = "Availability | SuPaw"
  const history = useHistory()
  const userDetails = useSelector(getUserDetails)
  const vetId = userDetails?._id

  const [availability, setAvailability] = useState(null)
  const [timing, setTiming] = useState(DEFAULT_TIMING)
  const [slotDuration, setSlotDuration] = useState(30)
  const [loading, setLoading] = useState(true)
  const [savingDefaults, setSavingDefaults] = useState(false)
  const [pageError, setPageError] = useState("")
  const [formError, setFormError] = useState("")
  const [formTouched, setFormTouched] = useState(false)
  const [dayModalOpen, setDayModalOpen] = useState(false)
  const [editingDay, setEditingDay] = useState(null)
  const [savingDay, setSavingDay] = useState(false)
  const [dayError, setDayError] = useState("")
  const [holidayModalOpen, setHolidayModalOpen] = useState(false)
  const [holidayDate, setHolidayDate] = useState("")
  const [holidayError, setHolidayError] = useState("")
  const [savingHoliday, setSavingHoliday] = useState(false)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const defaultErrors = useMemo(() => validateTiming(timing, false), [timing])
  const durationError = useMemo(() => {
    const value = Number(slotDuration)
    return !Number.isInteger(value) || value <= 0 || value > 1440
      ? "Duration must be a positive whole number of minutes (maximum 1440)."
      : ""
  }, [slotDuration])

  const applyAvailability = data => {
    setAvailability(data)
    setTiming({ ...DEFAULT_TIMING, ...(data?.defaultTiming || {}) })
    setSlotDuration(data?.slotDuration || 30)
  }

  const loadAvailability = async () => {
    if (!vetId) {
      setPageError(
        "Your Vet profile could not be identified. Please sign in again."
      )
      setLoading(false)
      return
    }
    try {
      setLoading(true)
      setPageError("")
      const response = requireSuccessfulResponse(
        await fetchProviderAvailability(PROVIDER_TYPE, vetId)
      )
      applyAvailability(response.data)
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAvailability()
  }, [vetId])

  const updateTiming = event => {
    const { name, value } = event.target
    setTiming(current => ({ ...current, [name]: value }))
    setFormTouched(true)
    setFormError("")
  }

  const saveDefaults = async event => {
    event.preventDefault()
    setFormTouched(true)
    setFormError("")
    if (Object.keys(defaultErrors).length || durationError) return
    try {
      setSavingDefaults(true)
      let latest = availability
      const timingChanged = Object.keys(DEFAULT_TIMING).some(
        key => timing[key] !== availability?.defaultTiming?.[key]
      )
      const durationChanged =
        Number(slotDuration) !== availability?.slotDuration
      if (timingChanged) {
        latest = requireSuccessfulResponse(
          await updateProviderDefaultTiming(PROVIDER_TYPE, vetId, timing)
        ).data
        setAvailability(latest)
        setTiming({ ...DEFAULT_TIMING, ...(latest?.defaultTiming || {}) })
      }
      if (durationChanged) {
        latest = requireSuccessfulResponse(
          await updateProviderSlotDuration(
            PROVIDER_TYPE,
            vetId,
            Number(slotDuration)
          )
        ).data
      }
      if (!timingChanged && !durationChanged) {
        showMessage({
          label: "Your availability is already up to date.",
          status: "success",
        })
        return
      }
      applyAvailability(latest)
      setFormTouched(false)
      showMessage({
        label: "Availability settings saved successfully.",
        status: "success",
      })
    } catch (error) {
      const message = getErrorMessage(error)
      setFormError(message)
      showMessage({ label: message, status: "error" })
    } finally {
      setSavingDefaults(false)
    }
  }

  const openDayModal = day => {
    setEditingDay(day || null)
    setDayError("")
    setDayModalOpen(true)
  }

  const saveDay = async values => {
    try {
      setSavingDay(true)
      setDayError("")
      const response = requireSuccessfulResponse(
        await saveProviderParticularDay(PROVIDER_TYPE, vetId, values)
      )
      applyAvailability(response.data)
      setDayModalOpen(false)
      showMessage({ label: response.message, status: "success" })
    } catch (error) {
      const message = getErrorMessage(error)
      setDayError(message)
      showMessage({ label: message, status: "error" })
    } finally {
      setSavingDay(false)
    }
  }

  const saveHoliday = async event => {
    event.preventDefault()
    if (!holidayDate) {
      setHolidayError("Please select a date.")
      return
    }
    try {
      setSavingHoliday(true)
      setHolidayError("")
      const response = requireSuccessfulResponse(
        await addProviderHoliday(PROVIDER_TYPE, vetId, holidayDate)
      )
      applyAvailability(response.data)
      setHolidayModalOpen(false)
      setHolidayDate("")
      showMessage({ label: response.message, status: "success" })
    } catch (error) {
      const message = getErrorMessage(error)
      setHolidayError(message)
      showMessage({ label: message, status: "error" })
    } finally {
      setSavingHoliday(false)
    }
  }

  const confirmDelete = async () => {
    if (!pendingDelete) return
    try {
      setDeleting(true)
      const response = requireSuccessfulResponse(
        pendingDelete.type === "day"
          ? await deleteProviderParticularDay(
              PROVIDER_TYPE,
              vetId,
              pendingDelete.date
            )
          : await deleteProviderHoliday(
              PROVIDER_TYPE,
              vetId,
              pendingDelete.date
            )
      )
      applyAvailability(response.data)
      setPendingDelete(null)
      showMessage({ label: response.message, status: "success" })
    } catch (error) {
      const message = getErrorMessage(error)
      setPendingDelete(null)
      showMessage({ label: message, status: "error" })
    } finally {
      setDeleting(false)
    }
  }

  const particularDays = [...(availability?.particularDays || [])].sort(
    (a, b) => a.date.localeCompare(b.date)
  )
  const holidays = [...(availability?.holidays || [])].sort()

  return (
    <div className="page-content availability-page">
      <Container fluid>
        <Row className="mb-4">
          <Col className="d-flex align-items-center gap-2">
            <img
              src={LeftArrow}
              width={32}
              onClick={() => history.goBack()}
              role="button"
              alt="Go back"
            />
            <div>
              <h4 className="mb-1 font-fontFamily fontSize-28">
                Operating Hours & Availability
              </h4>
              <p className="text-muted mb-0">
                Manage when customers can book appointments with you.
              </p>
            </div>
          </Col>
        </Row>

        {loading ? (
          <div className="availability-loading">
            <Spinner color="primary" />
            <span>Loading availability…</span>
          </div>
        ) : pageError ? (
          <Alert
            color="danger"
            className="d-flex justify-content-between align-items-center"
          >
            <span>{pageError}</span>
            <Button color="danger" outline size="sm" onClick={loadAvailability}>
              Try again
            </Button>
          </Alert>
        ) : (
          <>
            <Card className="availability-card">
              <CardBody>
                <div className="availability-card__heading">
                  <div>
                    <h5>Regular Availability</h5>
                    <p>
                      Your normal working hours, break, and appointment length.
                    </p>
                  </div>
                </div>
                <Form onSubmit={saveDefaults}>
                  {formError && <Alert color="danger">{formError}</Alert>}
                  <Row>
                    <Col lg={6} className="availability-field-group">
                      <h6>Default Working Hours</h6>
                      <Row>
                        <Col sm={6} className="mb-3">
                          <Label for="working-start">Start Time</Label>
                          <AvailabilityPicker
                            id="working-start"
                            name="startTime"
                            type="time"
                            value={timing.startTime}
                            onChange={updateTiming}
                            invalid={formTouched && !!defaultErrors.startTime}
                          />
                          {formTouched && defaultErrors.startTime && (
                            <div className="invalid-feedback d-block">
                              {defaultErrors.startTime}
                            </div>
                          )}
                        </Col>
                        <Col sm={6} className="mb-3">
                          <Label for="working-end">End Time</Label>
                          <AvailabilityPicker
                            id="working-end"
                            name="endTime"
                            type="time"
                            value={timing.endTime}
                            onChange={updateTiming}
                            invalid={formTouched && !!defaultErrors.endTime}
                          />
                          {formTouched && defaultErrors.endTime && (
                            <div className="invalid-feedback d-block">
                              {defaultErrors.endTime}
                            </div>
                          )}
                        </Col>
                      </Row>
                    </Col>
                    <Col lg={6} className="availability-field-group">
                      <h6>Lunch / Break</h6>
                      <Row>
                        <Col sm={6} className="mb-3">
                          <Label for="lunch-start">Start Time</Label>
                          <AvailabilityPicker
                            id="lunch-start"
                            name="lunchStart"
                            type="time"
                            value={timing.lunchStart}
                            onChange={updateTiming}
                            invalid={
                              formTouched &&
                              (!!defaultErrors.lunchStart ||
                                !!defaultErrors.lunchRange)
                            }
                          />
                          {formTouched &&
                            (defaultErrors.lunchStart ||
                              defaultErrors.lunchRange) && (
                              <div className="invalid-feedback d-block">
                                {defaultErrors.lunchStart ||
                                  defaultErrors.lunchRange}
                              </div>
                            )}
                        </Col>
                        <Col sm={6} className="mb-3">
                          <Label for="lunch-end">End Time</Label>
                          <AvailabilityPicker
                            id="lunch-end"
                            name="lunchEnd"
                            type="time"
                            value={timing.lunchEnd}
                            onChange={updateTiming}
                            invalid={
                              formTouched &&
                              (!!defaultErrors.lunchEnd ||
                                !!defaultErrors.lunchRange)
                            }
                          />
                          {formTouched &&
                            (defaultErrors.lunchEnd ||
                              defaultErrors.lunchRange) && (
                              <div className="invalid-feedback d-block">
                                {defaultErrors.lunchEnd ||
                                  defaultErrors.lunchRange}
                              </div>
                            )}
                        </Col>
                      </Row>
                    </Col>
                    <Col lg={6} className="availability-field-group mb-3">
                      <h6>Appointment Slot Duration</h6>
                      <Label for="slot-duration">Duration</Label>
                      <AvailabilityDurationInput
                        id="slot-duration"
                        value={slotDuration}
                        invalid={formTouched && !!durationError}
                        error={durationError}
                        onChange={value => {
                          setSlotDuration(value)
                          setFormTouched(true)
                          setFormError("")
                        }}
                      />
                      <small className="text-muted">
                        Enter any positive whole number of minutes.
                      </small>
                    </Col>
                  </Row>
                  <div className="d-flex justify-content-end">
                    <ButtonMain
                      className="btn btn-secondary"
                      type="submit"
                      isLoading={savingDefaults}
                      disabled={
                        savingDefaults ||
                        !!durationError ||
                        !!Object.keys(defaultErrors).length
                      }
                    >
                      Save Changes
                    </ButtonMain>
                  </div>
                </Form>
              </CardBody>
            </Card>

            <Card className="availability-card">
              <CardBody>
                <div className="availability-card__heading">
                  <div>
                    <h5>Particular Day Timings</h5>
                    <p>Override your regular hours for an exact date.</p>
                  </div>
                  <Button color="secondary" onClick={() => openDayModal()}>
                    <i className="mdi mdi-plus me-1" /> Add Particular Day
                  </Button>
                </div>
                {particularDays.length ? (
                  <div className="availability-list">
                    {particularDays.map(day => (
                      <div className="availability-list__item" key={day.date}>
                        <div>
                          <strong>{displayDate(day.date)}</strong>
                          <div>
                            {displayTime(day.startTime)} –{" "}
                            {displayTime(day.endTime)}
                          </div>
                          <small>
                            Lunch {displayTime(day.lunchStart)} –{" "}
                            {displayTime(day.lunchEnd)}
                          </small>
                        </div>
                        <div className="availability-list__actions">
                          <Button
                            color="link"
                            onClick={() => openDayModal(day)}
                          >
                            Edit
                          </Button>
                          <Button
                            color="link"
                            className="text-danger"
                            onClick={() =>
                              setPendingDelete({ type: "day", date: day.date })
                            }
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="availability-empty">
                    No particular-day timings configured.
                  </p>
                )}
              </CardBody>
            </Card>

            <Card className="availability-card">
              <CardBody>
                <div className="availability-card__heading">
                  <div>
                    <h5>Holidays</h5>
                    <p>Mark dates when no appointments should be available.</p>
                  </div>
                  <Button
                    color="secondary"
                    onClick={() => {
                      setHolidayError("")
                      setHolidayModalOpen(true)
                    }}
                  >
                    <i className="mdi mdi-plus me-1" /> Add Holiday
                  </Button>
                </div>
                {holidays.length ? (
                  <div className="availability-list">
                    {holidays.map(date => (
                      <div
                        className="availability-list__item availability-list__item--compact"
                        key={date}
                      >
                        <strong>{displayDate(date)}</strong>
                        <Button
                          color="link"
                          className="text-danger"
                          onClick={() =>
                            setPendingDelete({ type: "holiday", date })
                          }
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="availability-empty">No holidays configured.</p>
                )}
              </CardBody>
            </Card>
          </>
        )}
      </Container>

      <AvailabilityTimingModal
        isOpen={dayModalOpen}
        initialValue={editingDay}
        isSaving={savingDay}
        apiError={dayError}
        onClose={() => !savingDay && setDayModalOpen(false)}
        onSave={saveDay}
      />

      <Modal
        isOpen={holidayModalOpen}
        toggle={() => !savingHoliday && setHolidayModalOpen(false)}
        centered
      >
        <Form onSubmit={saveHoliday}>
          <ModalHeader
            toggle={() => !savingHoliday && setHolidayModalOpen(false)}
          >
            Add Holiday
          </ModalHeader>
          <ModalBody>
            {holidayError && <Alert color="danger">{holidayError}</Alert>}
            <Label for="holiday-date">Date</Label>
            <AvailabilityPicker
              id="holiday-date"
              name="date"
              type="date"
              value={holidayDate}
              onChange={event => {
                setHolidayDate(event.target.value)
                setHolidayError("")
              }}
              invalid={!!holidayError && !holidayDate}
            />
            {!!holidayError && !holidayDate && (
              <div className="invalid-feedback d-block">
                Please select a date.
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              type="button"
              onClick={() => setHolidayModalOpen(false)}
              disabled={savingHoliday}
            >
              Cancel
            </Button>
            <ButtonMain
              className="btn btn-secondary"
              type="submit"
              isLoading={savingHoliday}
              disabled={savingHoliday}
            >
              Add Holiday
            </ButtonMain>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal
        isOpen={!!pendingDelete}
        toggle={() => !deleting && setPendingDelete(null)}
        centered
      >
        <ModalHeader toggle={() => !deleting && setPendingDelete(null)}>
          Confirm removal
        </ModalHeader>
        <ModalBody>
          Are you sure you want to remove{" "}
          {pendingDelete?.type === "day"
            ? "the particular-day timing"
            : "the holiday"}{" "}
          for{" "}
          {pendingDelete?.date ? displayDate(pendingDelete.date) : "this date"}?
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            type="button"
            onClick={() => setPendingDelete(null)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <ButtonMain
            className="btn btn-danger"
            type="button"
            onClick={confirmDelete}
            isLoading={deleting}
            disabled={deleting}
          >
            Remove
          </ButtonMain>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default withRouter(Timings)
